import React, { useMemo } from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { QueryResponse, F910Record } from '../../../services/p9311Api';

interface ResultTableProps {
  result: QueryResponse;
  page: number;
  pageSize: number;
  onPageChange: (page: number, size: number) => void;
}

/** Map swipe type code to tag color (BR-F01). */
const swipeTypeColor: Record<string, string> = {
  Q: 'green',
  S: 'orange',
  T: 'blue',
  V: 'red',
};

/** Format HHMMSS string to HH:MM:SS display. */
const formatTime = (raw: string): string => {
  if (!raw || raw.length < 6) return raw || '';
  return `${raw.substring(0, 2)}:${raw.substring(2, 4)}:${raw.substring(4, 6)}`;
};

/** Format ISO date string to YYYY-MM-DD. */
const formatDate = (raw: string | null): string => {
  if (!raw) return '';
  return raw.substring(0, 10);
};

const columns: ColumnsType<F910Record> = [
  {
    title: '員工代號',
    dataIndex: 'f910_01',
    key: 'f910_01',
    width: 100,
    fixed: 'left',
    sorter: (a, b) => a.f910_01.localeCompare(b.f910_01),
  },
  {
    title: '員工姓名',
    dataIndex: 'f901_11',
    key: 'f901_11',
    width: 100,
  },
  {
    title: '部門代號',
    dataIndex: 'f901_32',
    key: 'f901_32',
    width: 90,
  },
  {
    title: '出勤日期',
    dataIndex: 'f910_02',
    key: 'f910_02',
    width: 110,
    render: (v: string | null) => formatDate(v),
    sorter: (a, b) => (a.f910_02 || '').localeCompare(b.f910_02 || ''),
  },
  {
    title: '刷卡類型',
    dataIndex: 'f910_11',
    key: 'f910_11',
    width: 90,
    align: 'center',
    render: (code: string, record: F910Record) => (
      <Tag color={swipeTypeColor[code] || 'default'}>
        {record.swipe_type_name || code}
      </Tag>
    ),
    filters: [
      { text: '上班 (Q)', value: 'Q' },
      { text: '外出 (S)', value: 'S' },
      { text: '進入 (T)', value: 'T' },
      { text: '下班 (V)', value: 'V' },
    ],
    onFilter: (value, record) => record.f910_11 === value,
  },
  {
    title: '刷卡機代號',
    dataIndex: 'f910_12',
    key: 'f910_12',
    width: 100,
    align: 'center',
  },
  {
    title: '刷卡時間',
    dataIndex: 'f910_13',
    key: 'f910_13',
    width: 100,
    align: 'center',
    render: (v: string) => formatTime(v),
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result, page, pageSize, onPageChange }) => {
  const rowKey = useMemo(() => {
    return (_record: F910Record, index?: number) =>
      `${_record.f910_01}_${_record.f910_02}_${_record.f910_11}_${_record.f910_13}_${index ?? 0}`;
  }, []);

  return (
    <div>
      <div className="mb-2 text-sm text-gray-500">
        共 {result.total} 筆出勤記錄
      </div>
      <Table<F910Record>
        dataSource={result.data}
        columns={columns}
        rowKey={rowKey}
        size="small"
        bordered
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: result.total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} 筆`,
          pageSizeOptions: ['25', '50', '100'],
          onChange: onPageChange,
        }}
      />
    </div>
  );
};

export default React.memo(ResultTable);
